import {
    BookText,
    Castle,
    CheckCircle2Icon,
    CircleAlertIcon,
    CircleIcon,
    CloudDownload,
    Delete,
    ImageIcon,
    Loader2Icon,
    MapIcon,
    SquareUserIcon,
    Swords,
    Users,
    WandSparkles,
} from "lucide-react";
import {Backup} from "@/backup/Backup.ts";
import {BackupCreationProgressEvent, RestoreEntityType, RestoreProgressEvent} from "@/backup/RestoreProgress.ts";
import {RestoreProgressPanel} from "@/backup/RestoreProgressPanel.tsx";
import {BackupStateBadge} from "@/backup/BackupStateBadge.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Progress} from "@/components/ui/progress.tsx";
import ConfirmDialog from "@/components/forms/ConfirmDialog.tsx";
import {cn} from "@/lib/utils.ts";
import {formatBackupDisplayName} from "@/backup/BackupPresentation.ts";

const backupMetrics = [
    {key: "users", type: "USERS", label: "Benutzer", icon: Users, iconClassName: "text-violet-700"},
    {key: "campaigns", type: "CAMPAIGNS", label: "Kampagnen", icon: BookText, iconClassName: "text-teal-800"},
    {key: "spells", type: "SPELLS", label: "Zauber", icon: WandSparkles, iconClassName: "text-sky-700"},
    {key: "characters", type: "CHARACTERS", label: "Charaktere", icon: SquareUserIcon, iconClassName: "text-sky-700"},
    {key: "scenes", type: "SCENES", label: "Szenen", icon: ImageIcon, iconClassName: "text-amber-600"},
    {key: "dungeons", type: "DUNGEONS", label: "Dungeons", icon: Castle, iconClassName: "text-emerald-800"},
    {key: "encounters", type: "ENCOUNTERS", label: "Encounter", icon: Swords, iconClassName: "text-red-900"},
] as const;

export function BackupDetails({backup, restoreProgress, progressOperation = "restore", busy, deleting, onRestore, onDelete}: {
    backup: Backup;
    restoreProgress: RestoreProgressEvent | BackupCreationProgressEvent | null;
    progressOperation?: "restore" | "creation";
    busy: boolean;
    deleting: boolean;
    onRestore: () => void;
    onDelete: () => void;
}) {
    const activeRestoreEntity = restoreProgress && (restoreProgress.status === "RESTORING" || restoreProgress.status === "EXPORTING")
        ? backupMetrics.find(({type}) => {
            const progress = restoreProgress.progress[type as RestoreEntityType];
            return progress && progress.processed < progress.total;
        })?.type
        : undefined;

    return <Card className="gap-0 overflow-hidden py-0">
        <CardHeader className="border-b px-4 py-3.5 sm:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <CardTitle className="truncate text-lg">{formatBackupDisplayName(backup)}</CardTitle>
                    <CardDescription className="mt-1 min-w-0">
                        <span className="block truncate" title={backup.filename}>{backup.filename}</span>
                        {backup.restoredAt && <span className="mt-0.5 block">
                            Zuletzt wiederhergestellt am {backup.restoredAt.toLocaleString("de-DE")}
                        </span>}
                    </CardDescription>
                </div>
                <BackupStateBadge state={backup.state} withIcon/>
            </div>
        </CardHeader>
        <CardContent className="px-4 py-4 sm:px-5">
            <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-medium">Enthaltener Datenbestand</h2>
                {restoreProgress && <span className="text-xs text-muted-foreground">Live-Fortschritt</span>}
            </div>
            {restoreProgress && <RestoreProgressPanel event={restoreProgress} embedded operation={progressOperation}/>}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {backupMetrics.map(({key, type, label, icon: Icon, iconClassName}) => {
                    const entityProgress = restoreProgress?.progress[type as RestoreEntityType];
                    const percentage = !entityProgress || entityProgress.total === 0
                        ? restoreProgress?.status === "SUCCEEDED" ? 100 : 0
                        : Math.round(entityProgress.processed / entityProgress.total * 100);
                    const completed = restoreProgress?.status === "SUCCEEDED"
                        || Boolean(entityProgress && entityProgress.total > 0 && entityProgress.processed >= entityProgress.total);
                    const active = type === activeRestoreEntity;
                    return <div key={key} className={cn(
                        "rounded-lg border bg-muted/20 p-2.5",
                        active && "border-primary/40 bg-primary/5"
                    )}>
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                                <Icon className={cn("size-4 shrink-0", iconClassName)}/>
                                <span className="truncate">{label}</span>
                            </div>
                            {restoreProgress && <span aria-label={completed ? "Abgeschlossen" : active ? "In Bearbeitung" : "Ausstehend"}>
                                {completed && <CheckCircle2Icon className="size-3.5 text-emerald-600"/>}
                                {active && <Loader2Icon className="size-3.5 animate-spin text-primary"/>}
                                {!completed && !active && <CircleIcon className="size-3.5 text-muted-foreground/60"/>}
                            </span>}
                        </div>
                        <div className="mt-1 text-lg font-semibold tabular-nums">{backup[key]}</div>
                        {restoreProgress && entityProgress && <div className="mt-2 space-y-1">
                            <Progress value={percentage} aria-label={`${label}: ${percentage} Prozent`}/>
                            <div className="text-right text-[11px] tabular-nums text-muted-foreground">
                                {entityProgress.processed} / {entityProgress.total}
                            </div>
                        </div>}
                    </div>;
                })}
            </div>
            {backup.error && <div role="alert"
                className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <CircleAlertIcon className="mt-0.5 size-4 shrink-0"/>{backup.error}
            </div>}
        </CardContent>
        <CardFooter className="flex flex-col-reverse justify-between gap-2 border-t px-4 py-3 sm:flex-row sm:px-5">
            <ConfirmDialog title="Backup löschen?" onConfirm={onDelete}
                           description="Der Vorgang ist permanent." confirm="Löschen">
                <Button type="button" variant="ghost"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={busy}>
                    {deleting ? <>Wird gelöscht<Loader2Icon className="animate-spin"/></> : <>Löschen<Delete/></>}
                </Button>
            </ConfirmDialog>
            <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild type="button" variant="outline">
                    <a href={`/api/backups/${backup.id}/download`} target="_blank" rel="noreferrer">
                        Herunterladen<CloudDownload/>
                    </a>
                </Button>
                <ConfirmDialog title="Sicherung einspielen?" onConfirm={onRestore}
                               description="Der vorhandene Datenbestand wird vollständig ersetzt. Bei einem Fehler werden alle Änderungen zurückgerollt."
                               confirm="Wiederherstellen">
                    <Button type="button" disabled={busy || backup.state === "FAILED"}>
                        Wiederherstellen<MapIcon/>
                    </Button>
                </ConfirmDialog>
            </div>
        </CardFooter>
    </Card>;
}
